import React, {useEffect, useState, useRef, useCallback} from "react";
import { connect } from "react-redux";
import { Spin, Modal, Button, Select, DatePicker, Input, Form, message } from "antd";
import moment from "moment";

import { getNews, getNewsTypes, getNewFlagCountsByType, createNews, deleteNews, updateNews, clearErrorMessage } from "../../reducers/newsReducer";
import CheckBox from "../../components/CheckBox/CheckBox";
import RichTextEditor from "../../components/RichTextEditor/RichTextEditor";
import { getFileType, isImageFile, getFilePreviewUrl, getFileIcon, hasFileIcon, getFileExt } from "../../utils/fileUtils";

import pdfIcon from "../../assets/img/pdf.svg";
import docIcon from "../../assets/img/doc.svg";
import xlsIcon from "../../assets/img/xls.svg";

import "./_news-materials.scss";

const NewMaterials = (props) => {
  const { news, loading } = props;
  const { getFieldDecorator } = props.form;
  const isMountedRef = useRef(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [activeTab, setActiveTab] = useState("published");
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [isNewsLoading, setIsNewsLoading] = useState(true);
  const [isNewsTypesLoading, setIsNewsTypesLoading] = useState(true);
  const [showInactiveMaterials, setShowInactiveMaterials] = useState(false);
  const [isPlayingVideo, setIsPlayingVideo] = useState(false);
  const [formData, setFormData] = useState({
    category: "",
    dateStart: "",
    dateEnd: "",
    title: "",
    link: "",
    image: null,
    imagePreview: null,
    description: "",
    files: [],
    filePreviews: {},
    imageError: "",
    filesError: ""
  });
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [submittingMode, setSubmittingMode] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);

  const ACCEPTABLE_IMAGE_FORMATS = ['jpeg', 'jpg', 'png'];
  const ACCEPTABLE_FILE_FORMATS = ['doc', 'docx', 'xls', 'xlsx', 'pdf', 'png', 'jpeg', 'jpg'];

  const hasFormDataChanged = () => {
    if (!initialFormData) return false;

    // Check all form fields
    if (formData.category !== initialFormData.category ||
      formData.dateStart !== initialFormData.dateStart ||
      formData.dateEnd !== initialFormData.dateEnd ||
      formData.title !== initialFormData.title ||
      formData.link !== initialFormData.link ||
      formData.description !== initialFormData.description) {
      return true;
    }

    // Check image changes
    if ((formData.image !== initialFormData.image) ||
      (formData.imagePreview !== initialFormData.imagePreview)) {
      return true;
    }

    // Check if main file was removed
    if ((!!formData.existingMainFile) !== (!!initialFormData.existingMainFile)) {
      return true;
    }

    // Check files changes (both length and actual files)
    if ((formData.files && formData.files.length) !== (initialFormData.files && initialFormData.files.length)) {
      return true;
    }

    // Check if existing attachment files were removed or modified
    const currentAttachmentCount = formData.existingAttachmentFiles ? formData.existingAttachmentFiles.length : 0;
    const initialAttachmentCount = initialFormData.existingAttachmentFiles ? initialFormData.existingAttachmentFiles.length : 0;
    if (currentAttachmentCount !== initialAttachmentCount) {
      return true;
    }

    // Check form description from Ant Form
    const currentDescription = props.form.getFieldValue('description') || '';
    const initialDescription = initialFormData.description || '';
    if (currentDescription !== initialDescription) {
      return true;
    }

    return false;
  };


  const isValidImageFormat = (fileName) => {
    const ext = getFileExt(fileName);
    return ACCEPTABLE_IMAGE_FORMATS.includes(ext);
  };

  const isValidFileFormat = (fileName) => {
    const ext = getFileExt(fileName);
    return ACCEPTABLE_FILE_FORMATS.includes(ext);
  };

  const formatDate = (dateString) => {
    const dateFormat = "DD/MM/YYYY";
    return moment(dateString).isValid() ? moment(dateString).format(dateFormat) : "";
  };


  const getFileUrl = (file, fileName = '') => {
    if (!file) return null;
    if (file.startsWith('data:') || file.startsWith('http')) {
      return file;
    }

    let mimeType = 'application/octet-stream';
    const extension = fileName.toLowerCase().split('.').pop();
    const mimeTypes = {
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'gif': 'image/gif',
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    };
    if (mimeTypes[extension]) {
      mimeType = mimeTypes[extension];
    }

    return `data:${mimeType};base64,${file}`;
  };

  const createMaterialObject = (newsItem) => ({
    id: newsItem.id,
    date: formatDate(newsItem.startDate),
    title: newsItem.title || "",
    desc: newsItem.newsDescription || "",
    image: newsItem.image,
    newsAttachmentFiles: newsItem.newsAttachmentFiles,
    endDate: newsItem.endDate,
    category: newsItem.typeId || "",
    dateStart: convertToDateInputFormat(newsItem.startDate || ""),
    dateEnd: convertToDateInputFormat(newsItem.endDate || ""),
    link: newsItem.youtubeLink || ""
  });

  const getDescriptionSummary = (description) => {
    const plainText = description.replace(/<[^>]*>/g, '');
    if (plainText.length > 500) {
      // Find the position of the first <table> tag in the HTML
      const tableTagMatch = description.match(/<table[\s>]/i);
      let cutIndex = null;
      if (tableTagMatch) {
        const tableIndex = tableTagMatch.index;
        let visibleLength = 0;
        let inTag = false;
        let tagBuffer = '';
        for (let i = 0; i < tableIndex; i++) {
          const char = description[i];
          if (char === '<') {
            inTag = true;
            tagBuffer = '<';
          } else if (char === '>' && inTag) {
            tagBuffer += '>';
            inTag = false;
            tagBuffer = '';
          } else if (inTag) {
            tagBuffer += char;
          } else {
            visibleLength++;
          }
        }
        if (visibleLength <= 500) {
          cutIndex = tableIndex;
        }
      }
      let visibleLength = 0;
      let truncatedHtml = '';
      let inTag = false;
      let tagBuffer = '';
      for (let i = 0; i < description.length; i++) {
        if (cutIndex !== null && i >= cutIndex) {
          truncatedHtml += '...';
          break;
        }
        const char = description[i];
        if (char === '<') {
          inTag = true;
          tagBuffer = '<';
        } else if (char === '>' && inTag) {
          tagBuffer += '>';
          truncatedHtml += tagBuffer;
          inTag = false;
          tagBuffer = '';
        } else if (inTag) {
          tagBuffer += char;
        } else {
          truncatedHtml += char;
          visibleLength++;
          if (cutIndex === null && visibleLength >= 500) {
            truncatedHtml += '...';
            break;
          }
        }
      }
      return truncatedHtml;
    }
    return description;
  };

  const shouldShowReadMore = (description) => {
    const plainText = description.replace(/<[^>]*>/g, '');
    return plainText.length > 500;
  };

  const convertToDateInputFormat = (dateString) => {
    if (!dateString) return "";
    if (dateString.includes("-")) return dateString;
    const parts = dateString.split("/");
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateString;
  };

  const isMaterialActive = (endDate) => {
    if (!endDate) return true;
    const endDateMoment = moment(endDate);
    const today = moment().startOf('day');
    return endDateMoment.isSameOrAfter(today);
  };

  const getYouTubeThumbnail = (url) => {
    let videoId = null;

    if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v');
    }

    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }
    return null;
  };

  const getVideoIdFromUrl = (url) => {
    let videoId = null;

    if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v');
    }

    return videoId;
  };

  const getYouTubeEmbedUrl = (url) => {
    const videoId = getVideoIdFromUrl(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1` : null;
  };

  const handleOptionsClick = (materialId, e) => {
    e.stopPropagation();
    setOpenDropdown(openDropdown === materialId ? null : materialId);
  };

  const getActualImagePreview = (material) => {
    if (
      material.newsAttachmentFiles &&
      Array.isArray(material.newsAttachmentFiles) &&
      material.newsAttachmentFiles.length > 0
    ) {
      const mainFile = material.newsAttachmentFiles.find(
        (file) => file.isMainFile === true
      );
      if (mainFile && mainFile.file) {
        if (mainFile.file.startsWith('data:') || mainFile.file.startsWith('http')) {
          return mainFile.file;
        }
        return `data:image/jpeg;base64,${mainFile.file}`;
      }
    }
    return material.image || null;
  };

  const removeFileHandler = (index, fileArray, fileKey, updateFn) => {
    const newFiles = Array.from(fileArray).filter((_, i) => i !== index);
    const dataTransfer = new DataTransfer();
    newFiles.forEach(f => dataTransfer.items.add(f));
    updateFn(fileKey, dataTransfer.files);

    if (fileKey === "files") {
      const newPreviews = { ...formData.filePreviews };
      delete newPreviews[fileArray[index].name];
      updateFn("filePreviews", newPreviews);
      document.getElementById("files").files = dataTransfer.files;
    }
  };

  const handleEdit = (material) => {
    const imagePreview = getActualImagePreview(material);

    const attachmentFiles = material.newsAttachmentFiles
      ? material.newsAttachmentFiles.filter(file => !file.isMainFile)
      : [];

    const existingMainFile = material.newsAttachmentFiles?.find(file => file.isMainFile);

    const editFormData = {
      id: material.id,
      category: material.category || "",
      dateStart: material.dateStart || convertToDateInputFormat(material.date || ""),
      dateEnd: material.dateEnd || "",
      title: material.title || "",
      link: material.link || "",
      image: material.image || null,
      imagePreview: imagePreview,
      description: material.desc || "",
      files: material.files || [],
      filePreviews: {},
      existingAttachmentFiles: attachmentFiles,
      existingMainFile: existingMainFile || null
    };

    setFormData(editFormData);
    setInitialFormData(editFormData);
    setHasFormChanges(false);
    setFormMode("edit");
    setIsFormModalVisible(true);
    setIsModalVisible(false);
  };

  const handleNewsActionSuccess = () => {
    setIsFormModalVisible(false);
    setHasFormChanges(false);
    setInitialFormData(null);
    props.form.resetFields();
    setCurrentPage(1);
    props.dispatch(getNews(1, 10, selectedCategory, !showInactiveMaterials, activeTab === "draft"));
  };

  const handleDelete = (materialId) => {
    Modal.confirm({
      title: "Ցանկանու՞մ եք հեռացնել նյութը",
      content: "Հեռացնելու դեպքում նյութը չի պահպանվի։",
      okText: "Հեռացնել նյութը",
      okType: "danger",
      cancelText: "Չեղարկել",
      onOk() {
        props.dispatch(deleteNews(materialId)).then(() => {
          if (isMountedRef.current) {
            setOpenDropdown(null);
            setIsModalVisible(false);
            setSelectedMaterial(null);
            setCurrentPage(1);
            props.dispatch(getNews(1, 10, selectedCategory, !showInactiveMaterials, activeTab === "draft"));
            props.dispatch(getNewFlagCountsByType());
          }
        }).catch((error) => {
          if (isMountedRef.current) {
            message.error(error.message || "Նյութի հեռացումը չի հաջողվել։\nԽնդրում ենք փորձել կրկին։");
          }
        });
      },
      onCancel() {
      },
    });
  };

  const handleAddNew = () => {
    const newFormData = {
      category: "",
      dateStart: "",
      dateEnd: "",
      title: "",
      link: "",
      image: null,
      imagePreview: null,
      description: "",
      files: [],
      filePreviews: {},
      imageError: "",
      filesError: "",
      existingAttachmentFiles: [],
      existingMainFile: null
    };
    setFormData(newFormData);
    setInitialFormData(newFormData);
    setHasFormChanges(false);
    props.form.resetFields();
    setFormMode("add");
    setIsFormModalVisible(true);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSelectedCategory(0);
    setShowInactiveMaterials(false);
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  const handleShowInactiveMaterials = (e) => {
    const isChecked = e.target.checked;
    setShowInactiveMaterials(isChecked);
    setCurrentPage(1);
  };

    const isFetchingRef = useRef(false);

    useEffect(() => {
        if (!loading.loading_state) {
            isFetchingRef.current = false;
        }
    }, [loading.loading_state]);

    const handleScroll = useCallback((event) => {
        const element = event.currentTarget;
        const scrollTop = element.scrollTop;
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;
        const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

        if (
            distanceFromBottom < 300 &&
            !loading.loading_state &&
            !isFetchingRef.current &&
            Array.isArray(news) &&
            news.length < props.totalRowCount
        ) {
            isFetchingRef.current = true;
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            props.dispatch(
                getNews(nextPage, 10, selectedCategory, !showInactiveMaterials, activeTab === "draft", true)
            );
        }
    }, [currentPage, loading.loading_state, news, selectedCategory, showInactiveMaterials, activeTab, props]);

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasFormChanges(true);
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result;
        const base64String = result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFormSubmit = async (isDraft) => {
    setSubmittingMode(isDraft ? "draft" : "publish");
    props.form.validateFields(async (err, values) => {
      if (!err) {
        const attachmentFiles = [];

        if (formData.image && typeof formData.image === 'object' && formData.image.size) {
          const imageBase64 = await fileToBase64(formData.image);
          attachmentFiles.push({
            Id: 0,
            File: imageBase64,
            FileName: formData.image.name,
            FileType: 0,
            IsMainFile: true
          });
        } else if (formData.existingMainFile && formData.imagePreview) {
          attachmentFiles.push({
            Id: formData.existingMainFile.id || 0,
            File: formData.existingMainFile.file || "",
            FileName: formData.existingMainFile.fileName,
            FileType: formData.existingMainFile.fileType || 0,
            IsMainFile: true
          });
        }

        if (formData.existingAttachmentFiles && formData.existingAttachmentFiles.length > 0) {
          formData.existingAttachmentFiles.forEach(file => {
            attachmentFiles.push({
              Id: file.id || 0,
              File: file.file || "",
              FileName: file.fileName,
              FileType: file.fileType || 0,
              IsMainFile: false
            });
          });
        }

        if (formData.files && formData.files.length > 0) {
          for (let i = 0; i < formData.files.length; i++) {
            if (typeof formData.files[i] === 'object' && formData.files[i].size) {
              const fileBase64 = await fileToBase64(formData.files[i]);
              attachmentFiles.push({
                Id: 0,
                File: fileBase64,
                FileName: formData.files[i].name,
                FileType: 0,
                IsMainFile: false
              });
            }
          }
        }

        const newsData = {
          Title: formData.title,
          NewsDescription: values.description,
          StartDate: formData.dateStart,
          EndDate: formData.dateEnd,
          PublishedDate: new Date().toISOString(),
          TypeId: values.category ? parseInt(values.category) : 0,
          YoutubeLink: formData.link,
          IsDraft: isDraft,
          NewsAttachmentFiles: attachmentFiles
        };

        const actionPromise = formMode === "add"
          ? props.dispatch(createNews(newsData))
          : props.dispatch(updateNews({ ...newsData, Id: formData.id }));

        actionPromise.then(() => {
          if (isMountedRef.current) {
            setSubmittingMode(null);
            handleNewsActionSuccess();
            props.dispatch(getNewFlagCountsByType());
          }
        }).catch((error) => {
          if (isMountedRef.current) {
            setSubmittingMode(null);
            message.error(error.message || "Կապի խնդիր. խնդրում ենք կրկին փորձել");
          }
        });
      } else {
        setSubmittingMode(null);
      }
    });
  };

  const handleFormModalClose = () => {
    if (hasFormDataChanged()) {
      Modal.confirm({
        title: "Ցանկանու՞մ եք փակել պատուհանը",
        content: "Փակելու դեպքում մուտքագրված տվյալները չեն պահպանվի",
        okText: "Փակել առանց պահպանելու",
        okType: "danger",
        cancelText: "Չեղարկել",
        onOk() {
          setIsFormModalVisible(false);
          setHasFormChanges(false);
          setInitialFormData(null);
          props.form.resetFields();
          setFormData({
            category: "",
            dateStart: "",
            dateEnd: "",
            title: "",
            link: "",
            image: null,
            imagePreview: null,
            description: "",
            files: [],
            filePreviews: {},
            imageError: "",
            filesError: "",
            existingAttachmentFiles: [],
            existingMainFile: null
          });
        },
        onCancel() {
        },
      });
    } else {
      setIsFormModalVisible(false);
      setHasFormChanges(false);
      setInitialFormData(null);
      props.form.resetFields();
      setFormData({
        category: "",
        dateStart: "",
        dateEnd: "",
        title: "",
        link: "",
        image: null,
        imagePreview: null,
        description: "",
        files: [],
        filePreviews: {},
        imageError: "",
        filesError: "",
        existingAttachmentFiles: [],
        existingMainFile: null
      });
    }
  };

  const handleMaterialClick = (material) => {
    setSelectedMaterial(material);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedMaterial(null);
    setIsPlayingVideo(false);
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (Array.isArray(news) && news !== undefined) {
      setIsNewsLoading(false);
    }
  }, [news]);

  useEffect(() => {
    if (loading.type === "GetNewsTypes") {
      setIsNewsTypesLoading(loading.loading_state);
    }
  }, [loading]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const materialOptions = event.target.closest('.material-options');
      if (!materialOptions) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (props.error_message.message && props.error_message.message !== "") {
      props.dispatch(clearErrorMessage());
    }
    props.dispatch(getNewsTypes());
    props.dispatch(getNewFlagCountsByType());
    setIsNewsTypesLoading(false);
  }, [props.dispatch]);

  useEffect(() => {
    if (isFormModalVisible && formData.id) {
      props.form.setFieldsValue({
        category: formData.category || undefined,
        dateStart: formData.dateStart ? moment(formData.dateStart) : undefined,
        dateEnd: formData.dateEnd ? moment(formData.dateEnd) : undefined,
        title: formData.title || undefined,
        link: formData.link || undefined,
        description: formData.description || ""
      });
    }
  }, [isFormModalVisible, formData.id]);

  useEffect(() => {
    setIsNewsLoading(true);
    setCurrentPage(1);
    props.dispatch(getNews(1, 10, selectedCategory, !showInactiveMaterials, activeTab === "draft"));
  }, [activeTab, selectedCategory, showInactiveMaterials]);

    useEffect(() => {
        const scrollElement = document.getElementById('scroll-container');
        if (scrollElement) {
            scrollElement.addEventListener("scroll", handleScroll);
        }
        return () => {
            if (scrollElement) {
                scrollElement.removeEventListener("scroll", handleScroll);
            }
        };
    }, [currentPage, loading, selectedCategory, showInactiveMaterials, activeTab, news]);

  const getNoMaterialsMessage = () => {
    if (activeTab === "draft") {
      return "Տվյալ պահին չկան խմբագրվող նյութեր";
    }
    if (showInactiveMaterials) {
      return "Տվյալ պահին չկան ոչ գործող նյութեր";
    }
    return "Տվյալ պահին չկան հրապարակված նյութեր";
  };

  const getNonMainFiles = (newsItem) => {
    if (newsItem.newsAttachmentFiles && Array.isArray(newsItem.newsAttachmentFiles)) {
      return newsItem.newsAttachmentFiles.filter(file => !file.isMainFile);
    }
    return [];
  };

  const getMainFileImageSrc = (newsItem) => {
    if (
      newsItem.newsAttachmentFiles &&
      Array.isArray(newsItem.newsAttachmentFiles) &&
      newsItem.newsAttachmentFiles.length > 0
    ) {
      const mainFile = newsItem.newsAttachmentFiles.find(
        (file) => file.isMainFile === true
      );
      if (mainFile && mainFile.file) {
        if (mainFile.file.startsWith('data:') || mainFile.file.startsWith('http')) {
          return mainFile.file;
        }
        return `data:image/jpeg;base64,${mainFile.file}`;
      }
    }
    const youtubeLink = newsItem.youtubeLink || newsItem.link;
    if (youtubeLink) {
      return getYouTubeThumbnail(youtubeLink);
    }
    return newsItem.image || null;
  };

  const hasImage = (newsItem) => {
    if (
      newsItem.newsAttachmentFiles &&
      Array.isArray(newsItem.newsAttachmentFiles) &&
      newsItem.newsAttachmentFiles.length > 0
    ) {
      const mainFile = newsItem.newsAttachmentFiles.find(
        (file) => file.isMainFile === true
      );
      if (mainFile && mainFile.file) {
        return true;
      }
    }
    const youtubeLink = newsItem.youtubeLink || newsItem.link;
    if (youtubeLink) {
      return true;
    }
    if (newsItem.image) {
      return true;
    }
    return false;
  };

  const isNewMaterial = (publishedDate) => {
    if (!publishedDate) return false;
    const pubDate = moment(publishedDate);
    const today = moment();
    if (pubDate.isAfter(today)) return false;
    const daysDifference = today.diff(pubDate, 'days');
    return daysDifference <= 15;
  };

  const getNewCountByCategory = (categoryKey) => {
    const newFlagCounts = props.newFlagCounts || {};
    return newFlagCounts[categoryKey] || 0;
  };

  return (
    <Spin spinning={isNewsLoading || isNewsTypesLoading}>
      <div>
        <div className="row">
          <div className="column small-12">
            <div className="flex-container row-wrap align-justify align-middle mb_40">
              <div className="h-title black md capitalize-first-letter">Տեղեկատվական նյութեր</div>
              {props.user_type === 4 && (
                <div className="btn-new" onClick={handleAddNew}>
                  <span>+</span> Նոր նյութ
                </div>
              )}
            </div>
          </div>
        </div>

        {props.user_type === 4 && (
          <div className="row">
            <div className="column small-12">
              <div className="material-tabs">
                <div
                  className={`material-tab cursor-pointer ${activeTab === "published" ? "active" : ""}`}
                  onClick={() => handleTabChange("published")}
                >
                  Հրապարակված
                </div>
                <div
                  className={`material-tab cursor-pointer ${activeTab === "draft" ? "active" : ""}`}
                  onClick={() => handleTabChange("draft")}
                >
                  Սևագիր
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "published" && (
          <div className="row">
            <div className="column small-12">
              <div className="material-categories">
                <div
                  className={`material-category cursor-pointer ${selectedCategory === 0 ? "active" : ""}`}
                  onClick={() => handleCategoryChange(0)}
                >
                  Բոլորը
                </div>
                {Array.isArray(props.newsTypes) &&
                  props.newsTypes.map((type) => (
                    <div
                      key={type.key}
                      className={`material-category cursor-pointer ${selectedCategory === type.key ? "active" : ""}`}
                      onClick={() => handleCategoryChange(type.key)}
                    >
                      {type.value}
                      {getNewCountByCategory(type.key) > 0 && (
                        <span>{getNewCountByCategory(type.key)}</span>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "published" && (
          <div className="row">
            <div className="column small-12">
              <CheckBox
                id="notActiveMaterial"
                name="notActiveMaterial"
                label="Ոչ գործող նյութ"
                type="checkbox"
                className="mb_25"
                onChange={handleShowInactiveMaterials}
              />
            </div>
          </div>
        )}
        <div className="row">
          <div className="small-12">
            {!isNewsLoading && Array.isArray(news) && news.length > 0 ? (
              news.map((newsItem, index) => (
                <div key={newsItem.id || index} className={`material ${!isMaterialActive(newsItem.endDate) ? 'material--not-active' : ''}`}>
                  {hasImage(newsItem) && (
                    <div className="material__img">
                      <img
                        src={getMainFileImageSrc(newsItem)}
                        alt="material"
                      />
                      {(newsItem.youtubeLink || newsItem.link) && (
                        <>
                          <div className="play-button-overlay"></div>
                          <div className="play-button">
                            <img
                              src={require("../../assets/img/play.svg")}
                              alt="play"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  <div
                    className="material__content cursor-pointer"
                    onClick={() =>
                      handleMaterialClick(createMaterialObject(newsItem))
                    }
                  >
                    <div className="material__top">
                      <div className="material__left">
                        {newsItem.publishedDate && (
                          <div className="material__date">
                            {formatDate(
                              newsItem.publishedDate
                            )}
                          </div>
                        )}
                        {isNewMaterial(newsItem.publishedDate) && (
                          <div className="material__tag material__tag--red">նոր</div>
                        )}
                        {!isMaterialActive(newsItem.endDate) && (
                          <div className="material__tag material__tag--gray">ոչ գործող</div>
                        )}
                      </div>

                      <div className="material__right">
                        {props.user_type === 4 && (
                          <div className="material-options">
                            <img
                              src={require("../../assets/img/options.svg")}
                              alt="options"
                              onClick={(e) => handleOptionsClick(newsItem.id, e)}
                              className="cursor-pointer"
                            />

                            {openDropdown === newsItem.id && (
                              <div className="material-options__dropdown">
                                <div
                                  className="material-options__item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit({
                                      id: newsItem.id,
                                      date: formatDate(
                                        newsItem.startDate
                                      ),
                                      title: newsItem.title || "",
                                      category: newsItem.typeId || "",
                                      dateStart: convertToDateInputFormat(
                                        newsItem.startDate || ""
                                      ),
                                      dateEnd: convertToDateInputFormat(
                                        newsItem.endDate || ""
                                      ),
                                      link: newsItem.youtubeLink || "",
                                      desc: newsItem.newsDescription || "",
                                      image: newsItem.image,
                                      newsAttachmentFiles: newsItem.newsAttachmentFiles
                                    });
                                  }}
                                >
                                  <img
                                    src={require("../../assets/img/edit.svg")}
                                    alt="edit"
                                  />
                                  <span> Խմբագրել</span>
                                </div>

                                <div
                                  className="material-options__item material-options__item--delete"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(newsItem.id);
                                  }}
                                >
                                  <img
                                    src={require("../../assets/img/trash.svg")}
                                    alt="trash"
                                  />
                                  <span>Հեռացնել</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {newsItem.title && (
                      <div className="material__title">
                        {newsItem.title}
                      </div>
                    )}

                    {newsItem.newsDescription && (
                      <div className="material__desc-wrapper">
                        <div
                          className="material__desc"
                          dangerouslySetInnerHTML={{
                            __html: getDescriptionSummary(newsItem.newsDescription),
                          }}
                        />
                        {shouldShowReadMore(newsItem.newsDescription) && (
                          <button
                            className="material__read-more-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMaterialClick(createMaterialObject(newsItem));
                            }}
                          >
                            <span>Կարդալ ավելին</span>
                            <img src={require("../../assets/img/chevron-right.svg")} alt="chevron-right" />
                          </button>
                        )}
                      </div>
                    )}

                    {getNonMainFiles(newsItem).length > 0 && (
                      <div className="materials-files-container">
                        <div className="materials-files-list">
                          {getNonMainFiles(newsItem).map((file, index) => {
                            const fileUrl = getFileUrl(file.file, file.fileName);
                            const isImage = isImageFile(file.fileName);

                            if (isImage && fileUrl) {
                              return (
                                <a
                                  key={index}
                                  href={fileUrl}
                                  download={file.fileName}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={file.fileName}
                                  className="materials-image cursor-pointer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <img
                                    src={fileUrl}
                                    alt={file.fileName}
                                  />
                                  <div className="materials-image__download">
                                    <img src={require("../../assets/img/download-white.svg")} alt="download" />
                                  </div>
                                </a>
                              );
                            } else if (fileUrl) {
                              return (
                                <a
                                  key={index}
                                  href={fileUrl}
                                  download={file.fileName}
                                  className="materials-file cursor-pointer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {getFileExt(file.fileName) === 'pdf' ? (
                                    <img
                                      src={pdfIcon}
                                      alt={getFileType(file.fileName)}
                                      className="materials-file__icon"
                                    />
                                  ) : getFileExt(file.fileName) === 'doc' || getFileExt(file.fileName) === 'docx' ? (
                                    <img
                                      src={docIcon}
                                      alt={getFileType(file.fileName)}
                                      className="materials-file__icon"
                                    />
                                  ) : getFileExt(file.fileName) === 'xls' || getFileExt(file.fileName) === 'xlsx' ? (
                                    <img
                                      src={xlsIcon}
                                      alt={getFileType(file.fileName)}
                                      className="materials-file__icon"
                                    />
                                  ) : null}
                                  <div className="materials-file__content">
                                    <span className="materials-file__name">
                                      {file.fileName}
                                    </span>
                                    <span className="materials-file__type">{getFileType(file.fileName).toUpperCase()}</span>
                                  </div>
                                  <div className="materials-file__download">
                                    <img src={require("../../assets/img/download.svg")} alt="download" />
                                  </div>
                                </a>
                              );
                            } else {
                              return (
                                <span key={index} className="materials-file__name">{file.fileName}</span>
                              );
                            }
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : !isNewsLoading ? (
              <div className="no-materials">
                <div className="no-materials__img"><img src={require("../../assets/img/no-materials.png")} alt="" /></div>
                <div className="no-materials__text">{getNoMaterialsMessage()}</div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <Modal
        className="material_modal"
        title={
          <div className="flex-container align-middle">
            <span className="materials-modal-header">
              {selectedMaterial && props.newsTypes?.find(type => type.key === selectedMaterial.category)?.value}
            </span>

            {props.user_type === 4 && (
              <div className="modal-options">
                <div
                  className="modal-option"
                  onClick={() => handleEdit(selectedMaterial)}
                >
                  <img
                    src={require("../../assets/img/edit.svg")}
                    alt="edit"
                  />
                  Խմբագրել
                </div>

                <div
                  className="modal-option modal-option--delete"
                  onClick={() => handleDelete(selectedMaterial?.id)}
                >
                  <img
                    src={require("../../assets/img/trash.svg")}
                    alt="trash"
                  />
                  Հեռացնել
                </div>
              </div>
            )}
          </div>
        }
        visible={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        centered
      >
        {selectedMaterial && (
          <div>
            {selectedMaterial.date && (
              <div className="materials-content-date">
                {selectedMaterial.date}
              </div>
            )}

            {selectedMaterial.title && (
              <div className="materials-content-title">
                {selectedMaterial.title}
              </div>
            )}

            {selectedMaterial.desc && (
              <div
                className="materials-content-desc"
                dangerouslySetInnerHTML={{
                  __html: selectedMaterial.desc,
                }}
              />
            )}

            {selectedMaterial && (getMainFileImageSrc(selectedMaterial) || selectedMaterial.image) && (
              <div className="materials-content-image">
                {!isPlayingVideo && (selectedMaterial.link || selectedMaterial.youtubeLink) ? (
                  <div
                    className="material-img-wrapper"
                    onClick={() => {
                      setIsPlayingVideo(true);
                    }}
                  >
                    <img
                      src={getMainFileImageSrc(selectedMaterial) || selectedMaterial.image}
                      alt="material"
                      className="cursor-pointer material-img-full-width"
                    />
                    <div className="play-button-overlay">
                      <div className="play-button">
                        <img
                          src={require("../../assets/img/play.svg")}
                          alt="play"
                        />
                      </div>
                    </div>
                  </div>
                ) : isPlayingVideo && (selectedMaterial.link || selectedMaterial.youtubeLink) ? (
                  <div className="youtube-iframe-container">
                    <iframe
                      className="youtube-iframe"
                      src={getYouTubeEmbedUrl(selectedMaterial.link || selectedMaterial.youtubeLink)}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="YouTube video"
                    />
                  </div>
                ) : (
                  <a
                    href={getMainFileImageSrc(selectedMaterial) || selectedMaterial.image}
                    download="material-image"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Click to download image"
                    className="inline-block material-img-full-width"
                  >
                    <div className="materials-content-image__download">
                      <img src={require("../../assets/img/download-white.svg")} alt="download" />
                    </div>
                    <img
                      src={getMainFileImageSrc(selectedMaterial) || selectedMaterial.image}
                      alt="material"
                      className="cursor-pointer material-img-full-width"
                    />
                  </a>
                )}
              </div>
            )}

            {selectedMaterial && getNonMainFiles(selectedMaterial).length > 0 && (
              <div className="materials-files-container">
                <div className="materials-files-list">
                  {getNonMainFiles(selectedMaterial).map((file, index) => {
                    const fileUrl = getFileUrl(file.file, file.fileName);
                    const isImage = isImageFile(file.fileName);

                    if (isImage && fileUrl) {
                      return (
                        <a
                          key={index}
                          href={fileUrl}
                          download={file.fileName}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={file.fileName}
                          className="materials-image"
                        >
                          <img
                            src={fileUrl}
                            alt={file.fileName}
                          />
                          <div className="materials-image__download">
                            <img src={require("../../assets/img/download-white.svg")} alt="download" />
                          </div>
                        </a>
                      );
                    } else if (fileUrl) {
                      return (
                        <a
                          key={index}
                          href={fileUrl}
                          download={file.fileName}
                          className="materials-file cursor-pointer"
                        >
                          {getFileExt(file.fileName) === 'pdf' ? (
                            <img
                              src={pdfIcon}
                              alt={getFileType(file.fileName)}
                              className="materials-file__icon"
                            />
                          ) : getFileExt(file.fileName) === 'doc' || getFileExt(file.fileName) === 'docx' ? (
                            <img
                              src={docIcon}
                              alt={getFileType(file.fileName)}
                              className="materials-file__icon"
                            />
                          ) : getFileExt(file.fileName) === 'xls' || getFileExt(file.fileName) === 'xlsx' ? (
                            <img
                              src={xlsIcon}
                              alt={getFileType(file.fileName)}
                              className="materials-file__icon"
                            />
                          ) : null}
                          <div className="materials-file__content">
                            <span className="materials-file__name">
                              {file.fileName}
                            </span>
                            <span className="materials-file__type">{getFileType(file.fileName).toUpperCase()}</span>
                          </div>
                          <div className="materials-file__download">
                            <img src={require("../../assets/img/download.svg")} alt="download" />
                          </div>
                        </a>
                      );
                    } else {
                      return (
                        <span key={index} className="materials-file__name">{file.fileName}</span>
                      );
                    }
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        className="material_form_modal"
        title={
          formMode === "add"
            ? "Նոր նյութի ստեղծում"
            : "Նյութի խմբագրում"
        }
        visible={isFormModalVisible}
        onCancel={handleFormModalClose}
        footer={[
          <Button key="draft" className="base_btn base_btn-border capitalize-first-letter" onClick={() => handleFormSubmit(true)} disabled={submittingMode !== null || (formMode === "edit" && activeTab === "published")} loading={submittingMode === "draft"}>
            Պահպանել որպես սևագիր
          </Button>,
          <Button key="submit" type="primary" className="base_btn base_btn-green capitalize-first-letter" onClick={() => handleFormSubmit(false)} disabled={submittingMode !== null} loading={submittingMode === "publish"}>
            Հրապարակել
          </Button>,
        ]}
        centered
      >
        <div className="material-form">
          <Form layout="vertical">
            <div className="row">
              <Form.Item label="Նյութի տեսակ" className="column small-12 large-4">
                {getFieldDecorator('category', {
                  initialValue: formData.category || undefined,
                  rules: [{ required: true, message: 'Ընտրեք նյութի տեսակը' }],
                })(
                  <Select
                    className="form-input"
                    onChange={(value) => {
                      handleFormChange("category", value);
                    }}
                    placeholder="Ընտրել"
                  >
                    {Array.isArray(props.newsTypes) && props.newsTypes.length > 0 && (
                      props.newsTypes.map((type) => (
                        <Select.Option key={type.key} value={type.key}>
                          {type.value}
                        </Select.Option>
                      ))
                    )}
                  </Select>
                )}
              </Form.Item>
              <Form.Item label="Սկիզբ" className="column small-12 large-4">
                {getFieldDecorator('dateStart', {
                  initialValue: formData.dateStart ? moment(formData.dateStart) : undefined,
                  rules: [{ required: true, message: 'Դաշտը լրացված չէ' }],
                })(
                  <DatePicker
                    className="form-input form-width-full"
                    format="DD/MM/YYYY"
                    placeholder="DD/MM/YYYY"
                    allowClear={false}
                    onChange={(date) => {
                      const formattedDate = date ? date.format("YYYY-MM-DD") : "";
                      handleFormChange("dateStart", formattedDate);
                      props.form.setFieldsValue({ dateStart: date });
                      setTimeout(() => {
                        props.form.validateFields(['dateEnd']);
                      }, 0);
                    }}
                  />
                )}
              </Form.Item>
              <Form.Item label="Ավարտ" className="column small-12 large-4">
                {getFieldDecorator('dateEnd', {
                  initialValue: formData.dateEnd ? moment(formData.dateEnd) : undefined,
                  rules: [
                    {
                      validator: (rule, value, callback) => {
                        const dateStart = props.form.getFieldValue('dateStart');
                        if (value && dateStart && value.isBefore(dateStart)) {
                          callback('«Ավարտ» ամսաթիվը չի կարող փոքր լինել «Սկիզբ» ամսաթվից։');
                        } else if (value && value.isBefore(moment().startOf('day'))) {
                          callback('Ավարտ ամսաթիվը չի կարող փոքր լինել Հրապարակման ամսաթվից։');
                        } else {
                          callback();
                        }
                      }
                    }
                  ]
                })(
                  <DatePicker
                    className="form-input form-width-full"
                    format="DD/MM/YYYY"
                    placeholder="DD/MM/YYYY"
                    allowClear={false}
                    onChange={(date) => handleFormChange("dateEnd", date ? date.format("YYYY-MM-DD") : "")}
                  />
                )}
              </Form.Item>
            </div>

            <Form.Item
              label={
                <div className="flex-container align-justify">
                  <span>Վերնագիր</span>
                </div>
              }
              className="column small-12"
              extra={
                <div className="flex-container align-justify">
                  <span>Առավելագույնը 106 սիմվոլ</span>
                  <span>{(formData.title?.length || 0)}/106</span>
                </div>
              }
            >
              {getFieldDecorator('title', {
                initialValue: formData.title || undefined,
                rules: [],
              })(
                <Input
                  type="text"
                  placeholder="Նշել վերնագիր"
                  maxLength={106}
                  onChange={(e) => {
                    handleFormChange("title", e.target.value);
                  }}
                />
              )}
            </Form.Item>

            <Form.Item label="Տեսանյութի հղում" className="column small-12">
              {getFieldDecorator('link', {
                initialValue: formData.link || undefined,
                rules: [
                  {
                    validator: (rule, value, callback) => {
                      if (!value) {
                        callback();
                      } else {
                        const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube|youtu|youtube-nocookie)\.(com|be)\//;
                        if (youtubeRegex.test(value)) {
                          callback();
                        } else {
                          callback('Խնդրում ենք կցել youtube-ի հղում');
                        }
                      }
                    }
                  }
                ]
              })(
                <Input
                  type="text"
                  placeholder="http//:"
                  disabled={!!(formData.image && typeof formData.image === 'object' && formData.image.size) || !!formData.imagePreview}
                  onChange={(e) => handleFormChange("link", e.target.value)}
                />
              )}
            </Form.Item>

            <Form.Item label="" className={`column small-12 ${formData.imageError ? 'has-error' : ''}`}>
              {!formData.imagePreview && (
                <label
                  htmlFor="image"
                  className={`file-upload-label ${formData.link ? 'disabled' : ''} ${formData.imageError ? 'has-error' : ''}`}
                >
                  <input
                    type="file"
                    className="file-upload-input"
                    id="image"
                    accept=".jpeg,.jpg,.png"
                    disabled={!!formData.link}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (!isValidImageFormat(file.name)) {
                          message.error("Թույլատրելի են միայն jpeg և png ֆորմատի ֆայլեր։");
                          e.target.value = "";
                          return;
                        }
                        handleFormChange("image", file);
                        const reader = new FileReader();
                        reader.readAsDataURL(file);
                        reader.onload = () => {
                          handleFormChange("imagePreview", reader.result);
                        };
                      }
                    }}
                  />
                  <div className="file-upload-label__content">
                    <img src={require("../../assets/img/img-placeholder.svg")} alt="upload" className="file-upload-label__icon" />
                    <div className="file-upload-label__info">
                      <span className="file-upload-label__title">Ընտրել գլխավոր նկար</span>
                      <span className="file-upload-label__desc">Ընդունելի ֆորմատներ՝ jpeg, png</span>
                    </div>
                  </div>
                </label>
              )}
              {formData.imageError && (
                <div className="ant-form-explain">
                  {formData.imageError}
                </div>
              )}
              {formData.imagePreview && (
                <div className="materials-image">
                  <img
                    src={formData.imagePreview}
                    alt="Preview"
                  />
                  <button
                    type="button"
                    className="materials-image__remove"
                    onClick={() => {
                      handleFormChange("image", null);
                      handleFormChange("imagePreview", null);
                      handleFormChange("existingMainFile", null);
                      const imageInput = document.getElementById("image");
                      if (imageInput) {
                        imageInput.value = "";
                      }
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}
            </Form.Item>

            <Form.Item label="Նկարագրություն" className="column small-12">
              {getFieldDecorator('description', {
                initialValue: formData.description || "",
                rules: [{
                  validator: (rule, value, callback) => {
                    const cleanContent = value ? value.replace(/<[^>]*>/g, "").trim() : "";
                    if (!cleanContent) {
                      callback("Դաշտը լրացված չէ");
                    } else if (cleanContent.length > 4000) {
                      callback("Նկարագրության առավելագույն թույլատրելի երկարությունը գերազանցված է:");
                    } else {
                      callback();
                    }
                  }
                }],
              })(
                <RichTextEditor
                  onChange={(content) => {
                    props.form.setFieldsValue({ description: content });
                  }}
                />
              )}
            </Form.Item>



            {((formData.existingAttachmentFiles && formData.existingAttachmentFiles.length > 0) ||
              (formData.files && formData.files.length > 0)) && (
                <Form.Item label="" className="column small-12">
                  <div className="materials-files-list">
                    {formData.existingAttachmentFiles && formData.existingAttachmentFiles.map((file, index) => {
                      const fileUrl = getFileUrl(file.file, file.fileName);
                      const isImage = isImageFile(file.fileName);

                      return (
                        <div key={`existing-${index}`} className={isImage ? "materials-image" : "materials-file"}>
                          {isImage && fileUrl ? (
                            <div className="materials-image">
                              <img
                                src={fileUrl}
                                alt={file.fileName}
                              />
                              <button
                                type="button"
                                className="materials-image__remove"
                                onClick={() => {
                                  const updatedFiles = formData.existingAttachmentFiles.filter((_, i) => i !== index);
                                  handleFormChange("existingAttachmentFiles", updatedFiles);
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <>
                              {getFileExt(file.fileName) === 'pdf' ? (
                                <img
                                  src={pdfIcon}
                                  alt={getFileType(file.fileName)}
                                  className="materials-file__icon"
                                />
                              ) : getFileExt(file.fileName) === 'doc' || getFileExt(file.fileName) === 'docx' ? (
                                <img
                                  src={docIcon}
                                  alt={getFileType(file.fileName)}
                                  className="materials-file__icon"
                                />
                              ) : getFileExt(file.fileName) === 'xls' || getFileExt(file.fileName) === 'xlsx' ? (
                                <img
                                  src={xlsIcon}
                                  alt={getFileType(file.fileName)}
                                  className="materials-file__icon"
                                />
                              ) : null}
                              <div className="materials-file__content">
                                <span className="materials-file__name">{file.fileName}</span>
                                <span className="materials-file__type">{getFileType(file.fileName).toUpperCase()}</span>
                              </div>

                              <button
                                type="button"
                                className="materials-file__remove"
                                onClick={() => {
                                  const updatedFiles = formData.existingAttachmentFiles.filter((_, i) => i !== index);
                                  handleFormChange("existingAttachmentFiles", updatedFiles);
                                }}
                              >
                                ✕
                              </button>
                            </>
                          )}
                        </div>
                      );
                    })}

                    {formData.files && Array.from(formData.files).map((file, index) => (
                      <div key={`new-${index}`} className={isImageFile(file.name) ? "materials-image" : "materials-file"}>
                        {isImageFile(file.name) && formData.filePreviews[file.name] ? (
                          <div className="materials-image">
                            <img
                              src={formData.filePreviews[file.name]}
                              alt={file.name}
                            />
                            <button
                              type="button"
                              className="materials-image__remove"
                              onClick={() => removeFileHandler(index, formData.files, "files", handleFormChange)}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <>
                            {getFileExt(file.name) === 'pdf' ? (
                              <img
                                src={pdfIcon}
                                alt={getFileType(file.name)}
                                className="materials-file__icon"
                              />
                            ) : getFileExt(file.name) === 'doc' || getFileExt(file.name) === 'docx' ? (
                              <img
                                src={docIcon}
                                alt={getFileType(file.name)}
                                className="materials-file__icon"
                              />
                            ) : getFileExt(file.name) === 'xls' || getFileExt(file.name) === 'xlsx' ? (
                              <img
                                src={xlsIcon}
                                alt={getFileType(file.name)}
                                className="materials-file__icon"
                              />
                            ) : null}
                            <div className="materials-file__content">
                              <span className="materials-file__name">{file.name}</span>
                              <span className="materials-file__type">{getFileType(file.name).toUpperCase()}</span>
                            </div>

                            <button
                              type="button"
                              className="materials-file__remove"
                              onClick={() => removeFileHandler(index, formData.files, "files", handleFormChange)}
                            >
                              ✕
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </Form.Item>
              )}

            <Form.Item label="" className={`column small-12 ${formData.filesError ? 'has-error' : ''}`}>
              <label
                htmlFor="files"
                className={`file-upload-label ${formData.filesError ? 'has-error' : ''}`}
              >
                <input
                  type="file"
                  className="file-upload-input"
                  id="files"
                  multiple
                  accept=".doc,.docx,.xls,.xlsx,.pdf,.png,.jpeg,.jpg"
                  onChange={(e) => {
                    e.persist();
                    const newFiles = e.target.files;
                    const dataTransfer = new DataTransfer();
                    const fileInputElement = document.getElementById("files");

                    let invalidFiles = [];
                    let validFiles = [];

                    for (let i = 0; i < newFiles.length; i++) {
                      if (isValidFileFormat(newFiles[i].name)) {
                        validFiles.push(newFiles[i]);
                      } else {
                        invalidFiles.push(newFiles[i].name);
                      }
                    }

                    if (invalidFiles.length > 0) {
                      const invalidFilesList = invalidFiles.join(', ');
                      message.error("Մեկ կամ մի քանի ֆայլեր չեն կցվել։ Թույլատրելի են միայն Word, Excel, PDF, Jpeg և Png ֆորմատի ֆայլեր։");
                    }

                    if (validFiles.length === 0) {
                      e.target.value = "";
                      return;
                    }

                    if (formData.files && formData.files.length > 0) {
                      for (let i = 0; i < formData.files.length; i++) {
                        dataTransfer.items.add(formData.files[i]);
                      }
                    }

                    for (let i = 0; i < validFiles.length; i++) {
                      dataTransfer.items.add(validFiles[i]);
                    }

                    const combinedFiles = dataTransfer.files;

                    const previews = { ...formData.filePreviews };
                    const promises = [];
                    for (let i = 0; i < combinedFiles.length; i++) {
                      if (isImageFile(combinedFiles[i].name) && !previews[combinedFiles[i].name]) {
                        promises.push(
                          getFilePreviewUrl(combinedFiles[i])
                            .then(preview => {
                              previews[combinedFiles[i].name] = preview;
                            })
                            .catch(error => {
                              console.error('Error generating preview:', error);
                            })
                        );
                      }
                    }

                    Promise.all(promises).then(() => {
                      handleFormChange("files", combinedFiles);
                      handleFormChange("filePreviews", previews);
                      if (fileInputElement) {
                        fileInputElement.value = "";
                      }
                    });
                  }}
                />
                <div className="file-upload-label__content">
                  <img src={require("../../assets/img/upload.svg")} alt="upload" className="file-upload-label__icon" />
                  <div className="file-upload-label__info">
                    <span className="file-upload-label__title">Վերբեռնել ֆայլեր</span>
                    <span className="file-upload-label__desc">Ընդունելի ֆորմատներ՝ word, excel, pdf, jpeg, png</span>
                  </div>
                </div>
              </label>
              {formData.filesError && (
                <div className="ant-form-explain">
                  {formData.filesError}
                </div>
              )}
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </Spin>
  );
};

const mapStateToProps = (state) => ({
  loading: state.newsReducer.loading,
  news: state.newsReducer.news || [],
  newsTypes: state.newsReducer.newsTypes || [],
  newFlagCounts: state.newsReducer.newFlagCounts || {},
  createdNewsId: state.newsReducer.createdNewsId,
  error_message: state.newsReducer.error_message,
  user_type: state.initReducer.user_type,
  totalRowCount: state.newsReducer.totalRowCount || 0
});

const NewMaterialsContainer = Form.create()(NewMaterials);

export default connect(mapStateToProps)(NewMaterialsContainer); 